import PropTypes from 'prop-types';
import { useState, useEffect } from 'react'; // Import useEffect
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, makeStyles,
} from '@material-ui/core';
import { useRouter } from 'next/router';
import { SearchStatus, TravelPolicyActions } from '../../../constants';
import MarkdownToHtml from '../../MarkdownToHtml';

const useStyles = makeStyles(() => ({
  root: {
    justifyContent: 'space-between',
  },
}));

const TravelPolicyConfirmationModal = ({
  tripId,
  message,
  onToggleModal,
  onConfirmClick,
  okButtonLabel,
  action,
}) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const classes = useStyles();
  const [textAreaValue, setTextAreaValue] = useState('');
  const [emailValue, setEmailValue] = useState(useSelector((store) => store.user.email));
  const { email: loggedInUserEmail } = useSelector((store) => store.user);

  const { saveSendTripEmailStatus, saveSendTripEmailError } = useSelector((store) => store.trip);

  const isRequestInProgress = action === TravelPolicyActions.RequiresApproval
    && saveSendTripEmailStatus === SearchStatus.SEARCHING;
  const isRequestDone = action === TravelPolicyActions.RequiresApproval
    && saveSendTripEmailStatus === SearchStatus.DONE;

  useEffect(() => {
    if (isRequestDone) {
      const timer = setTimeout(() => {
        onToggleModal();
        if (_.isEmpty(saveSendTripEmailError)) {
          router.reload();
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
    return null;
  }, [isRequestDone, saveSendTripEmailError, onToggleModal, router]);

  const handleConfirm = () => {
    if (action === TravelPolicyActions.RequiresApproval) {
      if (textAreaValue.trim() === '') {
        return;
      }
      onConfirmClick(textAreaValue, emailValue);
    } else {
      onConfirmClick();
    }
    track('TravelPolicyConfirmationModal.Confirmed', {
      loggedInUserEmail,
      page: window.location.search,
      tripId,
    });
  };

  const handleCancel = () => {
    onToggleModal();
    track('TravelPolicyConfirmationModal.Canceled');
  };

  const isTextAreaEmpty = textAreaValue.trim() === '';

  const renderContent = () => {
    if (!_.isEmpty(saveSendTripEmailError)) {
      return (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          {saveSendTripEmailError}
        </div>
      );
    }

    if (isRequestDone && action === TravelPolicyActions.RequiresApproval) {
      return (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4">
          Your booking request has been confirmed!
        </div>
      );
    }

    if (action === TravelPolicyActions.RequiresApproval) {
      return (
        <>
          <div className="flex items-center mb-4">
            <p className="text-sm text-gray-600 mr-3">Client Email:</p>
            <input
              type="email"
              className="flex-1 p-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email address"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
            />
          </div>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            placeholder="Enter your message for the advisor here..."
            value={textAreaValue}
            onChange={(e) => setTextAreaValue(e.target.value)}
            required
          />
          {isTextAreaEmpty && (
            <p className="text-red-500 text-sm mt-2">Message to advisor required!</p>
          )}
        </>
      );
    }

    return (
      <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4">
        {action === TravelPolicyActions.ShowMessageAndAllowBook && <b>Warning: </b>}
        <MarkdownToHtml content={message} className="text-current" />
      </div>
    );
  };

  const renderActions = () => {
    if (saveSendTripEmailError
      || (isRequestDone && action === TravelPolicyActions.RequiresApproval)) {
      return null;
    }

    if (action === TravelPolicyActions.RequiresApproval) {
      return (
        <>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            disabled={isRequestInProgress || isTextAreaEmpty}
          >
            {isRequestInProgress ? (
              <CircularProgress color="secondary" className="mr-4" size={15} />
            ) : (
              okButtonLabel
            )}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary">
          {okButtonLabel}
        </Button>
      </>
    );
  };

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      aria-labelledby="confirmation-dialog-title"
      open
    >
      <DialogContent dividers>
        {renderContent()}
      </DialogContent>
      <DialogActions className="justify-between" classes={classes}>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
};

TravelPolicyConfirmationModal.propTypes = {
  tripId: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  onConfirmClick: PropTypes.func.isRequired,
  okButtonLabel: PropTypes.string.isRequired,
  action: PropTypes.number.isRequired,
};

export default TravelPolicyConfirmationModal;
